import { useEffect, useId, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Clock3, ExternalLink, ImageIcon, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/form/Button";
import { MediaCarousel } from "@/components/MediaCarousel";
import { cn } from "@/utils/helpers";
import type { CalendarSlotIdea } from "@/types/contentCalendar.type";
import IdeaMediaDialog, { type MediaChoice } from "../concepts/IdeaMediaDialog";
import { briefVisualAsReference, resolvedFromChoice } from "./brief/briefVisualChoice";
import {
  isStaticBody,
  type BriefVisualPick,
  type BriefVisualSource,
  type CalendarBriefKind,
  type Distribution,
  type DistributionEntry,
  type ProductionBrief,
  type ProductionBriefBody,
  type ProductionDeliverable,
  type ProductionMeta,
  type ProductionPanel,
  type ProductionScheduleItem,
  type ProductionScriptCue,
  type ProductionStaticBody,
  type ResolvedBriefVisual,
  type ProductionVideoBody,
  type StoryboardScene,
} from "@/types/productionBrief.type";
import { Field, FieldLabel, TextArea, TextInput } from "./fields";
import { DISTRIBUTION_PLATFORM_LABEL } from "./DistributionSection";
import {
  blankScheduleItem,
  clearScheduleTiming,
  moveScheduleItem,
  parseScheduleTiming,
  removeScheduleItem,
  scheduleWindow,
  updateScheduleTiming,
  newScheduleRowId,
  withScheduleRowIds,
} from "./productionBriefSchedule";
import { posterFrameUrl } from "../videoThumbnails";

/**
 * The hand-editor for a production brief — every field on both body shapes.
 *
 * This is the only path that ever changes a brief once it HAS a body: the model
 * is never pointed at a written document, so a declined brief is repaired here or
 * not at all. That is why nothing is read-only, why the lists reorder, and why
 * the change note
 * sits in the save bar rather than behind a second dialog — the note is what the
 * client and the next operator read in the version rail.
 *
 * No API calls live here. The parent owns the mutation (see `useEditBrief`) so
 * it can hold the compare-and-swap `baseVersion` alongside the save.
 */

// ---------------------------------------------------------------- List helpers

/** Move an item within a list; a no-op past either end so the caller can stay dumb. */
function move<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

const cleanList = (items: string[]) => items.map((s) => s.trim()).filter(Boolean);

/** Trim every entry; drop platforms the operator emptied out entirely. */
function cleanDistribution(d: Distribution | undefined): Distribution {
  const out: Distribution = {};
  for (const [platform, entry] of Object.entries(d ?? {})) {
    const cleaned: DistributionEntry = {
      caption: entry.caption.trim(),
      hashtags: cleanList(entry.hashtags).map((h) => h.replace(/^#/, "")),
      ...(entry.title !== undefined ? { title: entry.title.trim() } : {}),
      ...(entry.description !== undefined ? { description: entry.description.trim() } : {}),
      ...(entry.tags !== undefined ? { tags: cleanList(entry.tags) } : {}),
    };
    if (cleaned.caption || cleaned.title || cleaned.description) out[platform] = cleaned;
  }
  return out;
}

type ListKey =
  | "conceptCues"
  | "conceptLock"
  | "props"
  | "wardrobe"
  | "locations"
  | "talent"
  | "crew"
  | "legalMandatories"
  | "palette"
  | "moodWords"
  | "doNots";

const LIST_META: Record<ListKey, { label: string; placeholder: string; addLabel: string }> = {
  conceptCues: { label: "Beats", placeholder: "What happens in this beat", addLabel: "Add beat" },
  conceptLock: { label: "Approved direction", placeholder: "A non-negotiable the team approved", addLabel: "Add lock" },
  props: { label: "Props", placeholder: "A prop to source", addLabel: "Add prop" },
  wardrobe: { label: "Wardrobe", placeholder: "A wardrobe item", addLabel: "Add item" },
  locations: { label: "Locations", placeholder: "A location", addLabel: "Add location" },
  talent: { label: "Talent & releases", placeholder: "Talent, casting or release requirement", addLabel: "Add talent note" },
  crew: { label: "Crew", placeholder: "Role and owner, e.g. DoP — Hadi R.", addLabel: "Add crew member" },
  legalMandatories: { label: "Brand & legal mandatories", placeholder: "A claim, logo, disclaimer or safe-zone rule", addLabel: "Add mandatory" },
  palette: { label: "Palette", placeholder: "e.g. #0B0B0B, warm sand", addLabel: "Add colour" },
  moodWords: { label: "Mood words", placeholder: "One word or phrase", addLabel: "Add mood word" },
  doNots: { label: "Do not", placeholder: "What the artwork must avoid", addLabel: "Add rule" },
};

function StringListEditor({
  listKey,
  items,
  onChange,
}: {
  listKey: ListKey;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const meta = LIST_META[listKey];
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // Enter should keep you typing, not make you reach for the mouse — the row it
  // lands on does not remount (keys are indices), so autoFocus cannot do this.
  //
  // A REF, not state: the pending index is never rendered, and holding it in
  // state means the effect that consumes it has to write state back, which costs
  // a cascading render (and trips react-hooks/set-state-in-effect) for a value
  // nothing displays.
  const focusAt = useRef<number | null>(null);

  useEffect(() => {
    if (focusAt.current === null) return;
    inputs.current[focusAt.current]?.focus();
    focusAt.current = null;
  });

  const insertAfter = (i: number) => {
    const next = [...items];
    next.splice(i + 1, 0, "");
    onChange(next);
    focusAt.current = i + 1;
  };

  const removeAt = (i: number) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <Field label={meta.label}>
      <div className="flex flex-col gap-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <TextInput
              ref={(el) => {
                inputs.current[i] = el;
              }}
              value={item}
              onChange={(e) => onChange(items.map((it, idx) => (idx === i ? e.target.value : it)))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  insertAfter(i);
                  return;
                }
                // Backspace on an already-empty row deletes it, the way a list
                // in any writing tool behaves.
                if (e.key === "Backspace" && item === "" && items.length > 1) {
                  e.preventDefault();
                  removeAt(i);
                  focusAt.current = Math.max(0, i - 1);
                }
              }}
              placeholder={meta.placeholder}
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={`Remove ${meta.label.toLowerCase()} ${i + 1}`}
              className="co-glass-control grid size-9 shrink-0 place-items-center rounded-pill text-co-faint transition hover:text-co-alert-ink"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            onChange([...items, ""]);
            focusAt.current = items.length;
          }}
          className="co-glass-control inline-flex h-8 w-fit items-center gap-1.5 rounded-pill px-3.5 text-[12.5px] font-medium text-co-text2 transition hover:text-co-text"
        >
          <Plus className="size-3.5" /> {meta.addLabel}
        </button>
      </div>
    </Field>
  );
}

// ------------------------------------------------------- Record list (scenes / panels)

/** The picker wiring a shot/panel row needs; deliverables and cues pass none. */
interface RowVisuals {
  canEdit: boolean;
  onChoose: (index: number) => void;
  onClear: (index: number) => void;
}

interface RowField<T> {
  key: Extract<keyof T, string>;
  label: string;
  placeholder: string;
  multiline?: boolean;
}

function isDirectImageUrl(value: string): boolean {
  const trimmed = value.trim();
  return /^data:image\//i.test(trimmed) || /\.(?:avif|gif|jpe?g|png|webp)(?:[?#].*)?$/i.test(trimmed);
}

function ReferenceImageWell({ url, alt }: { url: string; alt: string }) {
  const trimmedUrl = url.trim();
  const imageUrl = isDirectImageUrl(trimmedUrl) ? trimmedUrl : "";
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const failed = !!imageUrl && failedUrl === imageUrl;

  return (
    <div className="flex flex-col gap-2">
      <div className="relative aspect-[4/3] overflow-hidden rounded-card bg-[#121212] text-white">
        {imageUrl && !failed ? (
          <img
            src={imageUrl}
            alt={alt}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setFailedUrl(imageUrl)}
            className="size-full object-contain"
          />
        ) : (
          <div className="grid size-full place-items-center px-3 text-center">
            <div>
              <ImageIcon className="mx-auto size-4 text-white/60" strokeWidth={1.5} />
              <p className="tt-label mt-2 uppercase text-white/60">
                {failed ? "Visual could not load" : trimmedUrl ? "Reference linked" : "Visual not attached"}
              </p>
            </div>
          </div>
        )}
      </div>
      {/^https?:\/\//i.test(trimmedUrl) ? (
        <a
          href={trimmedUrl}
          target="_blank"
          rel="noreferrer"
          className="tt-label inline-flex w-fit max-w-full items-center gap-1.5 border-b border-co-line2 pb-0.5 uppercase text-co-accent-ink hover:border-co-accent"
        >
          <span className="truncate">Open source</span>
          <ExternalLink className="size-3 shrink-0" strokeWidth={1.8} />
        </a>
      ) : null}
    </div>
  );
}

/**
 * The canonical visual a shot or panel was given, from Brand Media, saved
 * References, Trend References or a pasted custom link (backend issue #70).
 *
 * Renders `resolvedVisual` — the URLs the server signs fresh on every read —
 * and never `visualSource`, which holds identity only. When the source row has
 * been deleted the server says so and the snapshot label still captions it,
 * which is the whole point of storing the label alongside the id.
 */
function BriefVisualWell({
  source,
  resolved,
  alt,
  canEdit,
  onChoose,
  onClear,
}: {
  source?: BriefVisualSource;
  resolved?: ResolvedBriefVisual;
  alt: string;
  canEdit: boolean;
  onChoose: () => void;
  onClear: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const label = resolved?.label ?? source?.label ?? "";
  const pageUrl = resolved?.pageUrl ?? source?.pageUrl ?? "";
  const unavailable = resolved?.unavailable ?? false;
  const still = resolved ? (resolved.imageUrl ?? resolved.thumbnailUrl) : null;

  /**
   * A trend example or a video reference has NO still of its own: `imageUrl` is
   * null and `thumbnailUrl` is the platform's own CDN link, which hotlink-blocks
   * and rots — so rendering it as an <img> reliably ends at "could not load".
   * The presigned `videoUrl` is our own mirror and always resolves, and a
   * preload="metadata" video paints its first frame, which is the still we
   * wanted. Same fallback order the concept media well uses.
   */
  const video = resolved && !resolved.unavailable ? resolved.videoUrl : null;
  const showVideo = !!video && (resolved?.mediaType === "video" || !still || failed);
  // A pasted carousel pages through its slides; every other pick is one picture.
  const slides = resolved && !resolved.unavailable ? (resolved.items ?? []) : [];

  return (
    <div className="flex flex-col gap-2">
      <div className="relative aspect-[4/3] overflow-hidden rounded-card bg-[#121212] text-white">
        {slides.length > 1 ? (
          <MediaCarousel items={slides} label={label || alt} />
        ) : showVideo ? (
          <video
            // `#t=` seeks to a frame and paints it, so the clip shows a still
            // instead of a black box. The external thumbnail is still preferred
            // as the poster on the occasions it loads.
            src={posterFrameUrl(video!)}
            muted
            playsInline
            preload="metadata"
            poster={still ?? undefined}
            className="size-full object-contain"
          />
        ) : still && !failed && !unavailable ? (
          <img
            src={still}
            alt={alt}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setFailed(true)}
            className="size-full object-contain"
          />
        ) : (
          <div className="grid size-full place-items-center px-3 text-center">
            <div>
              <ImageIcon className="mx-auto size-4 text-white/60" strokeWidth={1.5} />
              <p className="tt-label mt-2 uppercase text-white/60">
                {unavailable
                  ? "Source no longer available"
                  : source
                    ? failed
                      ? "Visual could not load"
                      : "Resolving visual"
                    : "No visual chosen"}
              </p>
            </div>
          </div>
        )}
        {slides.length <= 1 && resolved?.mediaType === "video" && !unavailable ? (
          <span className="tt-label absolute left-2 top-2 rounded-tag bg-black/70 px-1.5 py-0.5 uppercase text-white/80">
            Video
          </span>
        ) : null}
      </div>

      {label ? (
        <p className="truncate text-[11.5px] text-co-muted" title={label}>
          {label}
        </p>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            type="button"
            onClick={onChoose}
            className="tt-label w-fit border-b border-co-line2 pb-0.5 uppercase text-co-accent-ink transition-colors hover:border-co-accent"
          >
            {source ? "Replace visual" : "Choose visual"}
          </button>
          {source ? (
            <button
              type="button"
              onClick={onClear}
              className="tt-label w-fit border-b border-transparent pb-0.5 uppercase text-co-faint transition-colors hover:border-co-line2 hover:text-co-text"
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}

      {/^https?:\/\//i.test(pageUrl) ? (
        <a
          href={pageUrl}
          target="_blank"
          rel="noreferrer"
          className="tt-label inline-flex w-fit max-w-full items-center gap-1.5 border-b border-co-line2 pb-0.5 uppercase text-co-accent-ink hover:border-co-accent"
        >
          <span className="truncate">Open source</span>
          <ExternalLink className="size-3 shrink-0" strokeWidth={1.8} />
        </a>
      ) : null}
    </div>
  );
}

/**
 * The two reads/writes the generic row editor needs. An interface like
 * StoryboardScene has no index signature, so the cast is unavoidable — it lives
 * here, once, and the call sites keep full key inference.
 */
function readField<T extends object>(item: T, key: Extract<keyof T, string>): string {
  const value = item[key];
  return typeof value === "string" ? value : "";
}

function withField<T extends object>(
  item: T,
  key: Extract<keyof T, string>,
  value: string,
): T {
  return { ...item, [key]: value } as T;
}

/**
 * The storyboard / panel deck: an ordered list of small records. Order is the
 * edit here — a scene in the wrong place is the most common repair after a
 * decline — so every card carries its own move controls rather than hiding
 * reordering behind drag, which is unusable inside a scrolling modal.
 */
function RecordListEditor<T extends object>({
  label,
  noun,
  fields,
  items,
  blank,
  onChange,
  visuals,
}: {
  label: string;
  /** Singular, capitalised — heads each card and names the add button. */
  noun: string;
  fields: ReadonlyArray<RowField<T>>;
  items: T[];
  blank: () => T;
  onChange: (next: T[]) => void;
  /**
   * Present only for shots and panels, the two rows that can carry a picked
   * visual. Deliverables and script cues pass nothing and keep the plain card.
   */
  visuals?: RowVisuals;
}) {
  const sectionId = useId();
  const setField = (index: number, key: Extract<keyof T, string>, value: string) =>
    onChange(items.map((it, idx) => (idx === index ? withField(it, key, value) : it)));

  const moveRow = (from: number, to: number) => onChange(move(items, from, to));

  const removeRow = (index: number) =>
    onChange(items.filter((_, itemIndex) => itemIndex !== index));

  const addRow = () => onChange([...items, blank()]);

  return (
    <Field label={label}>
      <div className="flex flex-col gap-3">
        {items.length === 0 && (
          <p className="text-[12px] leading-relaxed text-co-faint">
            No {noun.toLowerCase()}s yet. Add the first one below.
          </p>
        )}

        {items.map((item, i) => {
          // Rows carry their own id (minted on add, backfilled on load, saved
          // with the body), so keys come off the data rather than a parallel
          // array that had to be spliced alongside every move and remove.
          const rowKey = (item as { id?: string }).id ?? `${sectionId}-row-fallback-${i}`;
          const referenceUrl = "referenceUrl" in item ? readField(item, "referenceUrl" as Extract<keyof T, string>) : "";
          return (
          <article
            key={rowKey}
            className="grid gap-4 border-y border-co-line bg-co-surface px-0 py-4 @[40rem]/briefeditor:grid-cols-[11rem_minmax(0,1fr)]"
          >
            <div className="flex min-w-0 flex-col gap-2 @[40rem]/briefeditor:border-r @[40rem]/briefeditor:border-co-line @[40rem]/briefeditor:pr-4">
              <div className="flex items-center justify-between @[40rem]/briefeditor:items-start">
              <span className="font-section text-[11px] font-semibold uppercase tracking-[0.14em] text-co-accent">
                {noun} {String(i + 1).padStart(2, "0")}
              </span>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  disabled={i === 0}
                  onClick={() => moveRow(i, i - 1)}
                  aria-label={`Move ${noun.toLowerCase()} ${i + 1} up`}
                  className="grid size-7 place-items-center rounded-pill text-co-faint transition hover:bg-co-hover hover:text-co-text disabled:pointer-events-none disabled:opacity-30"
                >
                  <ArrowUp className="size-3.5" />
                </button>
                <button
                  type="button"
                  disabled={i === items.length - 1}
                  onClick={() => moveRow(i, i + 1)}
                  aria-label={`Move ${noun.toLowerCase()} ${i + 1} down`}
                  className="grid size-7 place-items-center rounded-pill text-co-faint transition hover:bg-co-hover hover:text-co-text disabled:pointer-events-none disabled:opacity-30"
                >
                  <ArrowDown className="size-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  aria-label={`Remove ${noun.toLowerCase()} ${i + 1}`}
                  className="grid size-7 place-items-center rounded-pill text-co-faint transition hover:bg-co-hover hover:text-co-alert-ink"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
              </div>

              {visuals ? (
                <BriefVisualWell
                  source={(item as { visualSource?: BriefVisualSource }).visualSource}
                  resolved={(item as { resolvedVisual?: ResolvedBriefVisual }).resolvedVisual}
                  alt={`${noun} ${i + 1} visual`}
                  canEdit={visuals.canEdit}
                  onChoose={() => visuals.onChoose(i)}
                  onClear={() => visuals.onClear(i)}
                />
              ) : null}

              {/* The pasted-URL reference predates the picker and is kept for
                  the shots that still carry one. Shown only when there is no
                  picked visual, so a row never claims two. */}
              {"referenceUrl" in item &&
              referenceUrl &&
              !(item as { visualSource?: BriefVisualSource }).visualSource ? (
                <ReferenceImageWell
                  url={referenceUrl}
                  alt={`${noun} ${i + 1} visual reference`}
                />
              ) : null}
            </div>

            <div className="grid min-w-0 gap-x-3 gap-y-2.5 @[32rem]/briefeditor:grid-cols-2">
            {fields.map((f) => {
              const fieldId = `${rowKey}-${f.key}`;
              return (
                <div key={f.key} className={cn((f.multiline || f.key === "referenceUrl") && "@[32rem]/briefeditor:col-span-2")}>
                  <FieldLabel htmlFor={fieldId}>{f.label}</FieldLabel>
                  {f.multiline ? (
                    <TextArea
                      id={fieldId}
                      value={readField(item, f.key)}
                      onChange={(e) => setField(i, f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="min-h-[64px]"
                    />
                  ) : (
                    <TextInput
                      id={fieldId}
                      value={readField(item, f.key)}
                      onChange={(e) => setField(i, f.key, e.target.value)}
                      placeholder={f.placeholder}
                    />
                  )}
                </div>
              );
            })}
            </div>
          </article>
          );
        })}

        <button
          type="button"
          onClick={addRow}
          className="co-glass-control inline-flex h-8 w-fit items-center gap-1.5 rounded-pill px-3.5 text-[12.5px] font-medium text-co-text2 transition hover:text-co-text"
        >
          <Plus className="size-3.5" /> Add {noun.toLowerCase()}
        </button>
      </div>
    </Field>
  );
}

const SCENE_FIELDS: ReadonlyArray<RowField<StoryboardScene>> = [
  { key: "title", label: "Shot title", placeholder: "e.g. Freezer drawer opens" },
  { key: "referenceLabel", label: "Reference label", placeholder: "Source clip or visual reference" },
  { key: "referenceUrl", label: "Visual / reference URL", placeholder: "Direct image URL or source link" },
  { key: "visual", label: "Visual", placeholder: "What we see" },
  { key: "action", label: "Action", placeholder: "What happens" },
  // `framing` stays: it is what the model writes, and every shot that has one
  // keeps it. The split fields below are what a person fills in afterwards to
  // turn a sentence into something a camera department can run.
  { key: "framing", label: "Framing (summary)", placeholder: "e.g. 50mm, locked waist height" },
  { key: "shotSize", label: "Shot size", placeholder: "e.g. MCU, wide, extreme close" },
  { key: "cameraAngle", label: "Camera angle", placeholder: "e.g. eye level, low, overhead" },
  { key: "cameraMovement", label: "Camera movement", placeholder: "e.g. slow push in, locked off" },
  { key: "lens", label: "Lens", placeholder: "e.g. 35mm, 100mm macro" },
  { key: "equipment", label: "Equipment", placeholder: "e.g. slider, gimbal, probe lens" },
  { key: "duration", label: "Duration", placeholder: "e.g. 0:08" },
  { key: "location", label: "Location", placeholder: "Where this shot happens" },
  { key: "talent", label: "Talent", placeholder: "Who appears" },
  { key: "voiceover", label: "Voice-over", placeholder: "Spoken line, if any" },
  { key: "sound", label: "Sound", placeholder: "VO / SFX / music" },
  {
    key: "textOnScreen",
    label: "Text on screen",
    placeholder: "Exact overlay copy, or leave blank",
    multiline: true,
  },
  { key: "productionNote", label: "Production note", placeholder: "Crop safety, continuity or execution note", multiline: true },
];

const PANEL_FIELDS: ReadonlyArray<RowField<ProductionPanel>> = [
  { key: "role", label: "Role", placeholder: "e.g. Hook panel, proof panel, CTA panel" },
  { key: "referenceLabel", label: "Reference label", placeholder: "Source image or layout reference" },
  { key: "referenceUrl", label: "Visual / reference URL", placeholder: "Direct image URL or source link" },
  { key: "composition", label: "Composition", placeholder: "Framing and layout" },
  { key: "subject", label: "Subject", placeholder: "What is in frame" },
  { key: "copyOnPanel", label: "Copy on panel", placeholder: "The words printed on this panel" },
  { key: "aspectRatio", label: "Aspect ratio", placeholder: "e.g. 4:5" },
  { key: "dimensions", label: "Dimensions", placeholder: "e.g. 1080×1350" },
  { key: "assets", label: "Assets", placeholder: "Photography, packshot, illustration or source file" },
  { key: "retouching", label: "Retouching", placeholder: "Finishing and retouching notes" },
  {
    key: "imageryNote",
    label: "Imagery note",
    placeholder: "Direction for the shoot or the illustrator",
    multiline: true,
  },
];

const blankScene = (): StoryboardScene => ({
  id: newScheduleRowId(),
  visual: "",
  action: "",
  sound: "",
  textOnScreen: "",
});

const blankPanel = (): ProductionPanel => ({
  id: newScheduleRowId(),
  role: "",
  composition: "",
  subject: "",
  copyOnPanel: "",
  imageryNote: "",
});

/**
 * Backfill ids on shots/panels written before they existed, so the editor keys
 * off `id` and a reorder survives a reload rather than living in React state
 * that has to be spliced in lockstep with the data.
 */
function withRowIds<T extends { id?: string }>(rows: T[]): T[] {
  return rows.map((row) => (row.id ? { ...row } : { ...row, id: newScheduleRowId() }));
}

const DELIVERABLE_FIELDS: ReadonlyArray<RowField<ProductionDeliverable>> = [
  { key: "channel", label: "Channel", placeholder: "e.g. Instagram" },
  { key: "asset", label: "Asset", placeholder: "e.g. Reel master, carousel, story cutdown" },
  { key: "aspectRatio", label: "Aspect ratio", placeholder: "e.g. 9:16" },
  { key: "dimensions", label: "Dimensions", placeholder: "e.g. 1080×1920" },
  { key: "durationOrPages", label: "Duration / pages", placeholder: "e.g. 0:30 ×3 or 6 panels" },
  { key: "fileFormat", label: "File format", placeholder: "e.g. MP4 H.264 or PNG" },
  { key: "notes", label: "Production note", placeholder: "Crop, cover, safe-zone or export note", multiline: true },
];

const SCRIPT_FIELDS: ReadonlyArray<RowField<ProductionScriptCue>> = [
  { key: "timecode", label: "Timecode", placeholder: "e.g. 0:20" },
  { key: "line", label: "Line", placeholder: "The exact VO line", multiline: true },
];

const blankDeliverable = (): ProductionDeliverable => ({ id: newScheduleRowId(), channel: "", asset: "", aspectRatio: "", dimensions: "", durationOrPages: "", fileFormat: "", notes: "" });
const blankScriptCue = (): ProductionScriptCue => ({ id: newScheduleRowId(), timecode: "", line: "" });

// ---------------------------------------------------------------- Distribution

/**
 * The per-platform copy, editable. Distribution is part of what the client
 * signs off, so a repair after a decline has to be able to touch it — same
 * reason nothing else in this editor is read-only.
 */
function DistributionEditor({
  distribution,
  onChange,
}: {
  distribution: Distribution;
  onChange: (next: Distribution) => void;
}) {
  const patchEntry = (platform: string, p: Partial<DistributionEntry>) =>
    onChange({
      ...distribution,
      [platform]: { ...distribution[platform], ...p },
    });

  const platforms = Object.keys(distribution);
  if (platforms.length === 0) return null;

  return (
    <div className="flex flex-col gap-4 rounded-card border border-co-line bg-co-surface-solid p-3.5">
      <div className="flex flex-wrap items-end justify-between gap-2 border-b border-co-line pb-3">
        <p className="font-section text-[11px] font-semibold uppercase tracking-[0.14em] text-co-faint">
          Copy &amp; localization
        </p>
        <p className="text-[11px] text-co-faint">English is required. Add Arabic for bilingual posts.</p>
      </div>
      {platforms.map((platform) => {
        const entry = distribution[platform];
        const isYouTube = platform === "YOUTUBE";
        return (
          <div
            key={platform}
            className="flex flex-col gap-3 rounded-card border border-co-line bg-co-surface p-3"
          >
            <span className="font-section text-[11px] font-semibold uppercase tracking-[0.14em] text-co-accent">
              {DISTRIBUTION_PLATFORM_LABEL[platform] ?? platform}
            </span>

            {isYouTube && (
              <Field label="Title">
                <TextInput
                  dir="auto"
                  value={entry.title ?? ""}
                  onChange={(e) => patchEntry(platform, { title: e.target.value })}
                  placeholder="Video title"
                  maxLength={100}
                />
              </Field>
            )}

            {/* One field for every language the brand publishes in, a paragraph each. */}
            <Field label={isYouTube ? "Description" : "Caption"}>
              <TextArea
                dir="auto"
                value={isYouTube ? (entry.description ?? "") : entry.caption}
                onChange={(e) =>
                  patchEntry(
                    platform,
                    isYouTube
                      ? { description: e.target.value }
                      : { caption: e.target.value },
                  )
                }
                placeholder={isYouTube ? "Video description" : "The caption as it will be posted"}
              />
            </Field>

            <Field label={isYouTube ? "Tags (comma-separated)" : "Hashtags (comma-separated)"}>
              <TextInput
                value={(isYouTube ? (entry.tags ?? []) : entry.hashtags).join(", ")}
                onChange={(e) => {
                  const list = e.target.value
                    .split(",")
                    .map((t) => t.trim().replace(/^#/, ""))
                    .filter(Boolean);
                  patchEntry(platform, isYouTube ? { tags: list } : { hashtags: list });
                }}
                placeholder={isYouTube ? "coffee, roasting, dubai" : "coffee, specialtycoffee"}
              />
            </Field>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Draft plumbing

function cloneDistribution(d: Distribution | undefined): Distribution {
  const out: Distribution = {};
  for (const [platform, entry] of Object.entries(d ?? {})) {
    out[platform] = {
      ...entry,
      hashtags: [...entry.hashtags],
      ...(entry.tags ? { tags: [...entry.tags] } : {}),
    };
  }
  return out;
}

const EMPTY_PRODUCTION_META: ProductionMeta = {
  owner: "",
  productionLead: "",
  productionDate: "",
  internalReviewDate: "",
  clientDueDate: "",
};

function cloneProductionMeta(meta: ProductionMeta | undefined): ProductionMeta {
  return { ...EMPTY_PRODUCTION_META, ...meta };
}

function cleanProductionMeta(meta: ProductionMeta | undefined): ProductionMeta | undefined {
  if (!meta) return undefined;
  const cleaned = {
    owner: meta.owner.trim(),
    productionLead: meta.productionLead.trim(),
    productionDate: meta.productionDate.trim(),
    internalReviewDate: meta.internalReviewDate.trim(),
    clientDueDate: meta.clientDueDate.trim(),
  };
  return Object.values(cleaned).some(Boolean) ? cleaned : undefined;
}

/**
 * Trim the rows and drop the empty ones.
 *
 * Absent stays absent — the server treats "nobody filled this in" and "this was
 * filled in as nothing" as different facts, and an empty `startTime` would fail
 * its `HH:mm` check rather than read as "no time set". A row with a start but no
 * activity yet is half-typed, not empty, so it survives.
 */
function cleanSchedule(items: ProductionScheduleItem[] | undefined): ProductionScheduleItem[] {
  return (items ?? [])
    .map((item) => {
      const time = (item.time ?? "").trim();
      const startTime = (item.startTime ?? "").trim();
      return {
        ...(item.id ? { id: item.id } : {}),
        ...(time ? { time } : {}),
        ...(startTime ? { startTime } : {}),
        ...(typeof item.durationMinutes === "number" && item.durationMinutes > 0
          ? { durationMinutes: Math.round(item.durationMinutes) }
          : {}),
        activity: item.activity.trim(),
        owner: item.owner.trim(),
      };
    })
    .filter(
      (item) =>
        item.time || item.startTime || item.durationMinutes !== undefined || item.activity || item.owner,
    );
}

function cleanDeliverables(items: ProductionDeliverable[] | undefined): ProductionDeliverable[] {
  return (items ?? [])
    .map((item) => ({
      channel: item.channel.trim(),
      asset: item.asset.trim(),
      aspectRatio: item.aspectRatio.trim(),
      dimensions: item.dimensions.trim(),
      durationOrPages: item.durationOrPages.trim(),
      fileFormat: item.fileFormat.trim(),
      notes: item.notes.trim(),
    }))
    .filter((item) => Object.values(item).some(Boolean));
}

/** Deep-enough copy: every array and record the editor mutates gets its own identity. */
function cloneBody(kind: CalendarBriefKind, body: ProductionBriefBody): ProductionBriefBody {
  if (isStaticBody(kind, body)) {
    return {
      ...body,
      panels: withRowIds(body.panels),
      artDirection: {
        ...body.artDirection,
        palette: [...body.artDirection.palette],
        moodWords: [...body.artDirection.moodWords],
        doNots: [...body.artDirection.doNots],
      },
      distribution: cloneDistribution(body.distribution),
      props: [...body.props],
      wardrobe: [...body.wardrobe],
      locations: [...body.locations],
      conceptLock: [...(body.conceptLock ?? [])],
      productionMeta: cloneProductionMeta(body.productionMeta),
      talent: [...(body.talent ?? [])],
      crew: [...(body.crew ?? [])],
      // Backfill ids on rows written before they existed: the editor keys off
    // `id`, and the save round-trips it, so reorder survives a reload.
    schedule: withScheduleRowIds(body.schedule),
      deliverables: withRowIds(body.deliverables ?? []),
      legalMandatories: [...(body.legalMandatories ?? [])],
    };
  }
  return {
    ...body,
    conceptCues: [...body.conceptCues],
    storyboard: withRowIds(body.storyboard),
    distribution: cloneDistribution(body.distribution),
    props: [...body.props],
    wardrobe: [...body.wardrobe],
    locations: [...body.locations],
    conceptLock: [...(body.conceptLock ?? [])],
    productionMeta: cloneProductionMeta(body.productionMeta),
    talent: [...(body.talent ?? [])],
    crew: [...(body.crew ?? [])],
    // Backfill ids on rows written before they existed: the editor keys off
    // `id`, and the save round-trips it, so reorder survives a reload.
    schedule: withScheduleRowIds(body.schedule),
    script: withRowIds(body.script ?? []),
    deliverables: withRowIds(body.deliverables ?? []),
    legalMandatories: [...(body.legalMandatories ?? [])],
  };
}

/**
 * The saved body with some shots' or panels' visuals picked (null clears one),
 * for picks made outside the editor — the read view's "Choose visual". Applied
 * together, then cleaned once the same way an editor save is, so the indexes
 * all point at the rows as read and `resolvedVisual` never reaches a revision.
 */
export function bodyWithRowVisuals(
  kind: CalendarBriefKind,
  body: ProductionBriefBody,
  picks: ReadonlyMap<number, BriefVisualPick | null>,
): ProductionBriefBody {
  const draft = cloneBody(kind, body);
  const apply = <T extends { visualSource?: BriefVisualSource; resolvedVisual?: unknown }>(rows: T[]) =>
    rows.map((row, rowIndex) => {
      if (!picks.has(rowIndex)) return row;
      const pick = picks.get(rowIndex) ?? null;
      const next = { ...row };
      delete next.resolvedVisual;
      if (pick === null) delete next.visualSource;
      else next.visualSource = pick as BriefVisualSource;
      return next;
    });
  return cleanBody(
    kind,
    isStaticBody(kind, draft)
      ? { ...draft, panels: apply(draft.panels) }
      : { ...draft, storyboard: apply(draft.storyboard) },
  );
}

/** Trim everything and drop the rows the operator left blank. */
function cleanBody(kind: CalendarBriefKind, draft: ProductionBriefBody): ProductionBriefBody {
  if (isStaticBody(kind, draft)) {
    return {
      title: draft.title.trim(),
      synopsis: draft.synopsis.trim(),
      headline: draft.headline.trim(),
      // Field by field, so `resolvedVisual` is stripped — see the storyboard's
      // note below. Anything new has to be named here or it is lost on save.
      panels: draft.panels
        .map((p) => ({
          ...(p.id ? { id: p.id } : {}),
          ...(p.visualSource ? { visualSource: p.visualSource } : {}),
          role: p.role.trim(),
          composition: p.composition.trim(),
          subject: p.subject.trim(),
          copyOnPanel: p.copyOnPanel.trim(),
          imageryNote: p.imageryNote.trim(),
          ...(p.aspectRatio?.trim() ? { aspectRatio: p.aspectRatio.trim() } : {}),
          ...(p.dimensions?.trim() ? { dimensions: p.dimensions.trim() } : {}),
          ...(p.assets?.trim() ? { assets: p.assets.trim() } : {}),
          ...(p.retouching?.trim() ? { retouching: p.retouching.trim() } : {}),
          ...(p.referenceLabel?.trim() ? { referenceLabel: p.referenceLabel.trim() } : {}),
          ...(p.referenceUrl?.trim() ? { referenceUrl: p.referenceUrl.trim() } : {}),
        }))
        .filter((p) => Object.values(p).some(Boolean)),
      ctaTextOnArtwork: draft.ctaTextOnArtwork.trim(),
      artDirection: {
        palette: cleanList(draft.artDirection.palette),
        typography: draft.artDirection.typography.trim(),
        moodWords: cleanList(draft.artDirection.moodWords),
        doNots: cleanList(draft.artDirection.doNots),
      },
      caption: draft.caption.trim(),
      altText: draft.altText.trim(),
      distribution: cleanDistribution(draft.distribution),
      props: cleanList(draft.props),
      wardrobe: cleanList(draft.wardrobe),
      locations: cleanList(draft.locations),
      conceptLock: cleanList(draft.conceptLock ?? []),
      ...(cleanProductionMeta(draft.productionMeta) ? { productionMeta: cleanProductionMeta(draft.productionMeta) } : {}),
      talent: cleanList(draft.talent ?? []),
      crew: cleanList(draft.crew ?? []),
      schedule: cleanSchedule(draft.schedule),
      deliverables: cleanDeliverables(draft.deliverables),
      legalMandatories: cleanList(draft.legalMandatories ?? []),
    };
  }
  return {
    title: draft.title.trim(),
    synopsis: draft.synopsis.trim(),
    hookVo: draft.hookVo.trim(),
    conceptCues: cleanList(draft.conceptCues),
    ctaTextOnScreen: draft.ctaTextOnScreen.trim(),
    // Rebuilt field by field, so anything NOT named here is dropped on save.
    // That is what strips the read-only `resolvedVisual` (its URLs expire and
    // must never reach a revision) — and it is also why every new field has to
    // be listed explicitly, or the operator's typing vanishes on reload.
    storyboard: draft.storyboard
      .map((s) => ({
        ...(s.id ? { id: s.id } : {}),
        ...(s.title?.trim() ? { title: s.title.trim() } : {}),
        ...(s.visualSource ? { visualSource: s.visualSource } : {}),
        ...(s.referenceLabel?.trim() ? { referenceLabel: s.referenceLabel.trim() } : {}),
        ...(s.referenceUrl?.trim() ? { referenceUrl: s.referenceUrl.trim() } : {}),
        visual: s.visual.trim(),
        action: s.action.trim(),
        ...(s.framing?.trim() ? { framing: s.framing.trim() } : {}),
        ...(s.shotSize?.trim() ? { shotSize: s.shotSize.trim() } : {}),
        ...(s.cameraAngle?.trim() ? { cameraAngle: s.cameraAngle.trim() } : {}),
        ...(s.cameraMovement?.trim() ? { cameraMovement: s.cameraMovement.trim() } : {}),
        ...(s.lens?.trim() ? { lens: s.lens.trim() } : {}),
        ...(s.equipment?.trim() ? { equipment: s.equipment.trim() } : {}),
        ...(s.props?.length ? { props: cleanList(s.props) } : {}),
        ...(s.wardrobe?.length ? { wardrobe: cleanList(s.wardrobe) } : {}),
        ...(s.duration?.trim() ? { duration: s.duration.trim() } : {}),
        ...(s.location?.trim() ? { location: s.location.trim() } : {}),
        ...(s.talent?.trim() ? { talent: s.talent.trim() } : {}),
        ...(s.voiceover?.trim() ? { voiceover: s.voiceover.trim() } : {}),
        sound: s.sound.trim(),
        textOnScreen: s.textOnScreen.trim(),
        ...(s.productionNote?.trim() ? { productionNote: s.productionNote.trim() } : {}),
      }))
      .filter((s) => Object.values(s).some(Boolean)),
    distribution: cleanDistribution(draft.distribution),
    props: cleanList(draft.props),
    wardrobe: cleanList(draft.wardrobe),
    locations: cleanList(draft.locations),
    conceptLock: cleanList(draft.conceptLock ?? []),
    ...(cleanProductionMeta(draft.productionMeta) ? { productionMeta: cleanProductionMeta(draft.productionMeta) } : {}),
    talent: cleanList(draft.talent ?? []),
    crew: cleanList(draft.crew ?? []),
    schedule: cleanSchedule(draft.schedule),
    script: (draft.script ?? []).map((item) => ({ timecode: item.timecode.trim(), line: item.line.trim() })).filter((item) => item.timecode || item.line),
    ...(draft.soundDirection?.trim() ? { soundDirection: draft.soundDirection.trim() } : {}),
    deliverables: cleanDeliverables(draft.deliverables),
    legalMandatories: cleanList(draft.legalMandatories ?? []),
  };
}

// ---------------------------------------------------------------- Body shapes

function SourcingRow({
  draft,
  patch,
}: {
  draft: ProductionBriefBody;
  patch: (p: { props?: string[]; wardrobe?: string[]; locations?: string[] }) => void;
}) {
  return (
    <div className="grid gap-4 @[48rem]/briefeditor:grid-cols-3">
      <StringListEditor listKey="props" items={draft.props} onChange={(v) => patch({ props: v })} />
      <StringListEditor
        listKey="wardrobe"
        items={draft.wardrobe}
        onChange={(v) => patch({ wardrobe: v })}
      />
      <StringListEditor
        listKey="locations"
        items={draft.locations}
        onChange={(v) => patch({ locations: v })}
      />
    </div>
  );
}

function ProductionMetaEditor({
  meta,
  onChange,
}: {
  meta: ProductionMeta | undefined;
  onChange: (next: ProductionMeta) => void;
}) {
  const value = cloneProductionMeta(meta);
  const field = (key: keyof ProductionMeta, label: string, placeholder: string) => (
    <Field label={label}>
      <TextInput
        value={value[key]}
        onChange={(event) => onChange({ ...value, [key]: event.target.value })}
        placeholder={placeholder}
      />
    </Field>
  );
  return (
    <div className="grid gap-4 rounded-card border border-co-line bg-co-surface-solid p-3.5 @[36rem]/briefeditor:grid-cols-2 @[58rem]/briefeditor:grid-cols-3">
      <p className="font-section text-[11px] font-semibold uppercase tracking-[0.14em] text-co-faint @[36rem]/briefeditor:col-span-2 @[58rem]/briefeditor:col-span-3">
        Ownership &amp; dates
      </p>
      {field("owner", "Owner", "Account or project owner")}
      {field("productionLead", "Production lead", "Producer, designer or art director")}
      {field("productionDate", "Production date", "Shoot or design date")}
      {field("internalReviewDate", "Internal review", "Internal review date")}
      {field("clientDueDate", "Client due", "Client delivery date")}
    </div>
  );
}

function ScheduleEditor({
  items,
  onChange,
}: {
  items: ProductionScheduleItem[];
  onChange: (next: ProductionScheduleItem[]) => void;
}) {
  const sectionId = useId();
  const instructionId = `${sectionId}-instructions`;
  const [announcement, setAnnouncement] = useState("");
  // Rows carry their own id now (persisted, so a reorder survives a reload), so
  // React keys come off the data instead of a parallel array that had to be
  // spliced in lockstep with every move and remove.
  const firstTimedIndex = items.findIndex((item) => {
    const timing = parseScheduleTiming(item);
    return Boolean(timing.start && timing.durationMinutes);
  });

  const patchRow = (index: number, patch: Partial<ProductionScheduleItem>) =>
    onChange(items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)));

  const commitScheduleChange = (next: ProductionScheduleItem[], message: string) => {
    onChange(next);
    const window = scheduleWindow(next);
    setAnnouncement(
      window
        ? `${message} The production schedule now runs from ${window.start} to ${window.end}.`
        : message,
    );
  };

  const clearRowTiming = (index: number) => {
    const next = clearScheduleTiming(items, index);
    commitScheduleChange(
      next,
      `Production block ${index + 1} structured timing was cleared; remaining timed blocks were reflowed.`,
    );
  };

  const moveRow = (from: number, to: number) => {
    commitScheduleChange(
      moveScheduleItem(items, from, to),
      `Production block ${from + 1} moved to position ${to + 1}; timed blocks were reflowed.`,
    );
  };

  const removeRow = (index: number) => {
    commitScheduleChange(
      removeScheduleItem(items, index),
      `Production block ${index + 1} was removed; remaining timed blocks were reflowed.`,
    );
  };

  const addRow = () => {
    const window = scheduleWindow(items);
    // A new block starts where the last one ended, so the run of show stays
    // back-to-back without anyone retyping a clock.
    const next = [
      ...items,
      { ...blankScheduleItem(), ...(window?.end ? { startTime: window.end } : {}) },
    ];
    commitScheduleChange(next, `Production block ${next.length} was added.`);
  };

  return (
    <Field
      label="Production timing"
      hint={
        <span id={instructionId}>
          Set the first start and each estimated duration. Later starts and ends are derived so timed rows stay back-to-back. Clear an existing free-text time note before converting it; shot screen duration remains separate.
        </span>
      }
    >
      <div className="border-y border-co-line2">
        {items.length === 0 ? (
          <p className="py-4 text-[12px] leading-relaxed text-co-faint">
            No production blocks yet. Add the first block to build the run of show.
          </p>
        ) : null}

        {items.map((item, index) => {
          const timing = parseScheduleTiming(item);
          const durationValue = timing.durationMinutes?.toString() ?? "";
          const rowKey = item.id ?? `${sectionId}-row-fallback-${index}`;
          const legacyFreeText = timing.legacy && !timing.start;
          const derivedStart = timing.durationMinutes !== null && firstTimedIndex >= 0 && index !== firstTimedIndex;
          const startId = `${rowKey}-start`;
          const durationId = `${rowKey}-duration`;
          const endId = `${rowKey}-end`;
          const activityId = `${rowKey}-activity`;
          const ownerId = `${rowKey}-owner`;
          const legacyId = `${rowKey}-legacy`;
          return (
            <article key={rowKey} className="grid gap-3 border-b border-co-line py-4 last:border-b-0 @[40rem]/briefeditor:grid-cols-[3rem_minmax(0,1fr)]">
              <div className="flex items-center justify-between @[40rem]/briefeditor:flex-col @[40rem]/briefeditor:items-start @[40rem]/briefeditor:justify-start @[40rem]/briefeditor:gap-2">
                <span className="font-section text-[24px] font-extrabold tabular-nums leading-none text-co-text2">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => moveRow(index, index - 1)}
                    aria-label={`Move production block ${index + 1} up`}
                    className="grid size-7 place-items-center text-co-faint transition hover:text-co-text disabled:pointer-events-none disabled:opacity-30"
                  >
                    <ArrowUp className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={index === items.length - 1}
                    onClick={() => moveRow(index, index + 1)}
                    aria-label={`Move production block ${index + 1} down`}
                    className="grid size-7 place-items-center text-co-faint transition hover:text-co-text disabled:pointer-events-none disabled:opacity-30"
                  >
                    <ArrowDown className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    aria-label={`Remove production block ${index + 1}`}
                    className="grid size-7 place-items-center text-co-faint transition hover:text-co-alert-ink"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid min-w-0 gap-3 @[32rem]/briefeditor:grid-cols-2 @[58rem]/briefeditor:grid-cols-[8.5rem_8.5rem_8.5rem_minmax(12rem,1fr)_minmax(9rem,.65fr)]">
                <Field label={derivedStart ? "Start · derived" : "Start"} htmlFor={startId}>
                  <TextInput
                    id={startId}
                    type="time"
                    value={timing.start}
                    disabled={legacyFreeText}
                    readOnly={derivedStart}
                    aria-describedby={instructionId}
                    onChange={(event) => {
                      if (!event.target.value) {
                        clearRowTiming(index);
                        return;
                      }
                      const next = updateScheduleTiming(items, index, event.target.value, timing.durationMinutes);
                      commitScheduleChange(next, `Production block ${index + 1} timing was updated; timed blocks were reflowed.`);
                    }}
                    className="tabular-nums"
                  />
                  {timing.start ? (
                    <button
                      type="button"
                      onClick={() => clearRowTiming(index)}
                      className="tt-label mt-1.5 w-fit uppercase text-co-faint transition-colors hover:text-co-text"
                    >
                      Clear timing
                    </button>
                  ) : null}
                </Field>
                <Field label="Est. minutes" htmlFor={durationId}>
                  <TextInput
                    id={durationId}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={1440}
                    value={durationValue}
                    disabled={!timing.start}
                    aria-describedby={instructionId}
                    onChange={(event) => {
                      const parsed = Number(event.target.value);
                      const next = updateScheduleTiming(items, index, timing.start, Number.isFinite(parsed) && parsed > 0 ? parsed : null);
                      commitScheduleChange(next, `Production block ${index + 1} duration was updated; timed blocks were reflowed.`);
                    }}
                    placeholder="30"
                    className="tabular-nums"
                  />
                </Field>
                <Field label="Derived end" htmlFor={endId}>
                  <output id={endId} className="flex h-[43px] items-center rounded-card border border-co-line bg-co-ground px-3.5 text-[13px] tabular-nums text-co-muted">
                    <Clock3 className="mr-2 size-3.5 text-co-faint" strokeWidth={1.7} />
                    {timing.end || "Add duration"}
                  </output>
                </Field>
                <Field label="Activity" htmlFor={activityId}>
                  <TextInput
                    id={activityId}
                    value={item.activity}
                    onChange={(event) => patchRow(index, { activity: event.target.value })}
                    placeholder="What happens in this block"
                  />
                </Field>
                <Field label="Owner" htmlFor={ownerId}>
                  <TextInput
                    id={ownerId}
                    value={item.owner}
                    onChange={(event) => patchRow(index, { owner: event.target.value })}
                    placeholder="Person or discipline"
                  />
                </Field>
                {legacyFreeText ? (
                  <div className="@[32rem]/briefeditor:col-span-2 @[58rem]/briefeditor:col-span-5">
                    <Field label="Existing time note" htmlFor={legacyId} hint="This older free-text value is preserved. Clear it only when you are ready to replace it with structured timing.">
                      <TextInput id={legacyId} value={item.time ?? ""} onChange={(event) => patchRow(index, { time: event.target.value })} />
                    </Field>
                  </div>
                ) : null}
              </div>
            </article>
          );
        })}

        <button
          type="button"
          onClick={addRow}
          className="co-glass-control my-3 inline-flex h-8 w-fit items-center gap-1.5 rounded-pill px-3.5 text-[12.5px] font-medium text-co-text2 transition hover:text-co-text"
        >
          <Plus className="size-3.5" /> Add production block
        </button>
      </div>
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
    </Field>
  );
}

type SharedProductionPatch = Partial<
  Pick<
    ProductionVideoBody,
    | "conceptLock"
    | "productionMeta"
    | "props"
    | "wardrobe"
    | "locations"
    | "talent"
    | "crew"
    | "schedule"
    | "deliverables"
    | "legalMandatories"
  >
>;

function BriefFoundationFields({
  draft,
  patch,
}: {
  draft: ProductionBriefBody;
  patch: (next: SharedProductionPatch) => void;
}) {
  return (
    <>
      <StringListEditor listKey="conceptLock" items={draft.conceptLock ?? []} onChange={(value) => patch({ conceptLock: value })} />
      <ProductionMetaEditor meta={draft.productionMeta} onChange={(productionMeta) => patch({ productionMeta })} />
      <ScheduleEditor items={draft.schedule ?? []} onChange={(schedule) => patch({ schedule })} />
    </>
  );
}

function OperationsFields({
  draft,
  patch,
}: {
  draft: ProductionBriefBody;
  patch: (next: SharedProductionPatch) => void;
}) {
  return (
    <>
      <SourcingRow draft={draft} patch={patch} />
      <div className="grid gap-4 @[36rem]/briefeditor:grid-cols-2">
        <StringListEditor listKey="talent" items={draft.talent ?? []} onChange={(value) => patch({ talent: value })} />
        <StringListEditor listKey="crew" items={draft.crew ?? []} onChange={(value) => patch({ crew: value })} />
      </div>
      <StringListEditor listKey="legalMandatories" items={draft.legalMandatories ?? []} onChange={(value) => patch({ legalMandatories: value })} />
      <RecordListEditor<ProductionDeliverable>
        label="Deliverables & crops"
        noun="Deliverable"
        fields={DELIVERABLE_FIELDS}
        items={draft.deliverables ?? []}
        blank={blankDeliverable}
        onChange={(deliverables) => patch({ deliverables })}
      />
    </>
  );
}

function VideoFields({
  draft,
  onChange,
  visuals,
}: {
  draft: ProductionVideoBody;
  onChange: (next: ProductionVideoBody) => void;
  visuals: RowVisuals;
}) {
  const patch = (p: Partial<ProductionVideoBody>) => onChange({ ...draft, ...p });

  return (
    <>
      <Field label="Title">
        <TextInput
          value={draft.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="Brief title"
        />
      </Field>
      <Field label="Synopsis">
        <TextArea
          value={draft.synopsis}
          onChange={(e) => patch({ synopsis: e.target.value })}
          placeholder="One-line logline"
        />
      </Field>
      <BriefFoundationFields draft={draft} patch={patch} />
      <Field label="Hook — first 3 seconds">
        <TextArea
          value={draft.hookVo}
          onChange={(e) => patch({ hookVo: e.target.value })}
          placeholder="The opening hook, as it is said"
        />
      </Field>

      <StringListEditor
        listKey="conceptCues"
        items={draft.conceptCues}
        onChange={(v) => patch({ conceptCues: v })}
      />

      <RecordListEditor<StoryboardScene>
        label="Shot plan"
        noun="Shot"
        fields={SCENE_FIELDS}
        items={draft.storyboard}
        blank={blankScene}
        onChange={(storyboard) => patch({ storyboard })}
        visuals={visuals}
      />

      <RecordListEditor<ProductionScriptCue>
        label="VO script"
        noun="Cue"
        fields={SCRIPT_FIELDS}
        items={draft.script ?? []}
        blank={blankScriptCue}
        onChange={(script) => patch({ script })}
      />

      <Field label="Overall sound direction">
        <TextArea
          value={draft.soundDirection ?? ""}
          onChange={(e) => patch({ soundDirection: e.target.value })}
          placeholder="Music, location sound, SFX and mix direction"
        />
      </Field>

      <Field label="CTA — text on screen">
        <TextInput
          value={draft.ctaTextOnScreen}
          onChange={(e) => patch({ ctaTextOnScreen: e.target.value })}
          placeholder="Closing call to action"
        />
      </Field>

      <OperationsFields draft={draft} patch={patch} />

      {draft.distribution && Object.keys(draft.distribution).length > 0 && (
        <DistributionEditor
          distribution={draft.distribution}
          onChange={(distribution) => patch({ distribution })}
        />
      )}

    </>
  );
}

function StaticFields({
  draft,
  onChange,
  visuals,
}: {
  draft: ProductionStaticBody;
  onChange: (next: ProductionStaticBody) => void;
  visuals: RowVisuals;
}) {
  const patch = (p: Partial<ProductionStaticBody>) => onChange({ ...draft, ...p });
  const patchArt = (p: Partial<ProductionStaticBody["artDirection"]>) =>
    onChange({ ...draft, artDirection: { ...draft.artDirection, ...p } });

  return (
    <>
      <Field label="Title">
        <TextInput
          value={draft.title}
          onChange={(e) => patch({ title: e.target.value })}
          placeholder="Brief title"
        />
      </Field>
      <Field label="Synopsis">
        <TextArea
          value={draft.synopsis}
          onChange={(e) => patch({ synopsis: e.target.value })}
          placeholder="One-line logline"
        />
      </Field>
      <BriefFoundationFields draft={draft} patch={patch} />
      <Field label="Headline" hint="The line that carries the artwork.">
        <TextInput
          value={draft.headline}
          onChange={(e) => patch({ headline: e.target.value })}
          placeholder="The headline as it will be set"
        />
      </Field>

      <RecordListEditor<ProductionPanel>
        label="Panel deck"
        noun="Panel"
        fields={PANEL_FIELDS}
        items={draft.panels}
        blank={blankPanel}
        onChange={(panels) => patch({ panels })}
        visuals={visuals}
      />

      <Field label="CTA — on artwork">
        <TextInput
          value={draft.ctaTextOnArtwork}
          onChange={(e) => patch({ ctaTextOnArtwork: e.target.value })}
          placeholder="Closing call to action"
        />
      </Field>

      {/* Art direction — the four rails a designer works inside. */}
      <div className="flex flex-col gap-4 rounded-card border border-co-line bg-co-surface-solid p-3.5">
        <p className="font-section text-[11px] font-semibold uppercase tracking-[0.14em] text-co-faint">
          Art direction
        </p>
        <StringListEditor
          listKey="palette"
          items={draft.artDirection.palette}
          onChange={(v) => patchArt({ palette: v })}
        />
        <Field label="Typography">
          <TextInput
            value={draft.artDirection.typography}
            onChange={(e) => patchArt({ typography: e.target.value })}
            placeholder="e.g. Display face for the headline, sans serif for body"
          />
        </Field>
        <div className="grid gap-4 @[36rem]/briefeditor:grid-cols-2">
          <StringListEditor
            listKey="moodWords"
            items={draft.artDirection.moodWords}
            onChange={(v) => patchArt({ moodWords: v })}
          />
          <StringListEditor
            listKey="doNots"
            items={draft.artDirection.doNots}
            onChange={(v) => patchArt({ doNots: v })}
          />
        </div>
      </div>

      <OperationsFields draft={draft} patch={patch} />

      <Field label="Master caption">
        <TextArea
          dir="auto"
          value={draft.caption}
          onChange={(e) => patch({ caption: e.target.value })}
          placeholder="The caption as it will be posted"
        />
      </Field>
      <Field label="Alt text" hint="What a screen reader says in place of the artwork.">
        <TextArea
          value={draft.altText}
          onChange={(e) => patch({ altText: e.target.value })}
          placeholder="Describe the artwork"
          className="min-h-[64px]"
        />
      </Field>

      {draft.distribution && Object.keys(draft.distribution).length > 0 && (
        <DistributionEditor
          distribution={draft.distribution}
          onChange={(distribution) => patch({ distribution })}
        />
      )}

    </>
  );
}

// ---------------------------------------------------------------- Editor

interface Props {
  /**
   * Only what the editor reads: which shape, and the version it resets on. A
   * workshop idea's brief has no slot, status or send history, and needs none.
   */
  brief: Pick<ProductionBrief, "id" | "kind" | "version">;
  /** Whose libraries the per-shot visual picker offers. */
  brandId: string;
  /** The live body to edit — the parent reads it off the newest revision. */
  body: ProductionBriefBody;
  onSave: (body: ProductionBriefBody, changeNote: string | undefined) => Promise<unknown>;
  onCancel: () => void;
  saving: boolean;
  className?: string;
  /** The idea the brief is written from: its trends fill the visual picker's Trend tab. */
  idea?: Pick<CalendarSlotIdea, "reference" | "sources" | "aspectRatio"> | null;
}

/**
 * The picture picker for one shot or slide: the Change media dialog in its
 * brief mode, named for the row and opened on what the row shows now. The
 * editor and the read view's "Choose a picture" both open it.
 */
export function BriefVisualDialog({
  brandId,
  kind,
  body,
  index,
  idea = null,
  onClose,
  onConfirm,
}: {
  brandId: string;
  kind: CalendarBriefKind;
  body: ProductionBriefBody;
  index: number;
  idea?: Pick<CalendarSlotIdea, "reference" | "sources" | "aspectRatio"> | null;
  onClose: () => void;
  /** The confirmed picture; null takes it off. */
  onConfirm: (choice: MediaChoice | null) => void;
}) {
  const isStatic = isStaticBody(kind, body);
  const row = isStatic ? body.panels[index] : body.storyboard[index];
  const name = (isStatic ? body.panels[index]?.role : body.storyboard[index]?.title)?.trim() || null;
  const noun = isStatic ? `Picture ${index + 1}` : `Shot ${String(index + 1).padStart(2, "0")}`;
  const current = row ? briefVisualAsReference(row.visualSource, row.resolvedVisual) : null;
  return (
    <IdeaMediaDialog
      brandId={brandId}
      isOpen
      mode="visual"
      title={current ? "Change the picture" : "Choose a picture"}
      description={`${noun}${name ? `, ${name}` : ""}. Only the picture changes; its words stay as they are.`}
      ideaLabel={null}
      plannedRatio={idea?.aspectRatio ?? ""}
      current={current}
      sources={idea?.sources ?? []}
      ideaReference={idea?.reference ?? null}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}

export default function ProductionBriefEditor({
  brief,
  brandId,
  body,
  onSave,
  onCancel,
  saving,
  className,
  idea = null,
}: Props) {
  const noteId = useId();
  const [draft, setDraft] = useState<ProductionBriefBody>(() => cloneBody(brief.kind, body));
  const [changeNote, setChangeNote] = useState("");
  /** Which shot/panel the picker is open for. Null = closed. */
  const [pickingRow, setPickingRow] = useState<number | null>(null);

  // Reset on a new brief or a new version, and deliberately NOT on every `body`
  // identity: the slot query polls, and a refetch that returns the same version
  // must not wipe half-typed repairs out from under the operator.
  useEffect(() => {
    setDraft(cloneBody(brief.kind, body));
    setChangeNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief.id, brief.version]);

  const handleSave = () => {
    const note = changeNote.trim();
    void onSave(cleanBody(brief.kind, draft), note || undefined).catch(() => undefined);
  };

  /**
   * Write a pick onto one shot/panel, or clear it.
   *
   * The saved row keeps IDENTITY ONLY — `{ kind, mediaId }` and friends; the
   * server rebuilds the label, permalink and media type from the source row.
   * The well shows the picker's own picture meanwhile (`resolvedVisual`, which
   * `cleanBody` strips before the save), and the save hands back fresh URLs.
   */
  const setRowVisual = (index: number, choice: MediaChoice | null) => {
    const pick: BriefVisualPick | null = choice?.pick ?? null;
    setDraft((current) => {
      const apply = <T extends { visualSource?: BriefVisualSource; resolvedVisual?: ResolvedBriefVisual }>(
        rows: T[],
      ) =>
        rows.map((row, rowIndex) => {
          if (rowIndex !== index) return row;
          const next = { ...row };
          delete next.resolvedVisual;
          if (pick === null) delete next.visualSource;
          else {
            next.visualSource = pick as BriefVisualSource;
            if (choice) next.resolvedVisual = resolvedFromChoice(choice);
          }
          return next;
        });
      return isStaticBody(brief.kind, current)
        ? { ...current, panels: apply(current.panels) }
        : { ...current, storyboard: apply(current.storyboard) };
    });
    setPickingRow(null);
  };

  const rowVisuals = {
    canEdit: true,
    onChoose: (index: number) => setPickingRow(index),
    onClear: (index: number) => setRowVisual(index, null),
  };

  return (
    <div
      className={cn(
        "ws-brief-editor @container/briefeditor flex grow shrink-0 flex-col gap-4 rounded-panel border border-co-accent-border bg-co-surface p-4",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="leading-tight">
          <p className="font-section text-[12px] font-semibold uppercase tracking-[0.16em] text-co-accent">
            Editing the brief
          </p>
          <p className="mt-1 text-[11.5px] text-co-faint">
            Version {brief.version} · saving writes version {brief.version + 1}
          </p>
        </div>
      </div>

      {isStaticBody(brief.kind, draft) ? (
        <StaticFields draft={draft} onChange={setDraft} visuals={rowVisuals} />
      ) : (
        <VideoFields draft={draft} onChange={setDraft} visuals={rowVisuals} />
      )}

      {/* The Change media dialog the ideas use, with Brand media first, so a
          shot's picture and an idea's media are chosen the same way. */}
      {pickingRow !== null ? (
        <BriefVisualDialog
          brandId={brandId}
          kind={brief.kind}
          body={draft}
          index={pickingRow}
          idea={idea}
          onClose={() => setPickingRow(null)}
          onConfirm={(choice) => setRowVisual(pickingRow, choice)}
        />
      ) : null}

      {/* Save bar. Sticky because the static body runs long and the operator
          should never have to scroll to the bottom to get out of the editor. */}
      <div className="sticky bottom-0 z-10 mt-auto -mx-4 -mb-4 flex flex-col gap-1 border-t border-co-line bg-co-surface-solid px-4 pt-2.5 pb-2">
        <div className="flex items-center gap-2.5">
          <label
            htmlFor={noteId}
            className="shrink-0 font-section text-[11px] font-semibold uppercase tracking-[0.12em] text-co-faint"
          >
            Note
          </label>
          <TextInput
            id={noteId}
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            placeholder="what changed?"
            maxLength={200}
            disabled={saving}
            className="m-0 h-9 w-auto min-w-0 flex-1 py-0 text-[13px]"
          />
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="ghost" size="small" onPress={onCancel} isDisabled={saving}>
              Cancel
            </Button>
            <Button size="small" icon={Save} onPress={handleSave} loading={saving}>
              Save brief
            </Button>
          </div>
        </div>
        <p className="text-[11px] leading-snug text-co-faint">
          Optional, but the note is the line everyone reads in the version rail later.
        </p>
      </div>
    </div>
  );
}
